import { CaretLeftIcon } from "@phosphor-icons/react/dist/csr/CaretLeft";
import { CaretRightIcon } from "@phosphor-icons/react/dist/csr/CaretRight";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@volca/ui/components/ui/badge";
import { LogoIcon } from "@volca/ui/components/ui/logo";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@volca/ui/components/ui/sidebar";
import { cn } from "@volca/ui/lib/utils";
import { CommandMenuTrigger } from "./command-menu-trigger";
import { MobileSectionRow as SectionRow } from "./mobile-section-row";
import { useNavColumn } from "./nav-column-state";
import type { NavGroupId } from "./nav-items";
import { railHasAttention, railRowGroups, type RailEntry } from "./rail-model";
import { SidebarNavSkeleton } from "./sidebar-nav-skeleton";
import { SidebarNavSupport } from "./sidebar-nav-support";
import { SIDEBAR_ICON_CURRENT } from "./sidebar-row";

const GROUP_LABEL: Record<NavGroupId, string> = {
  referrals: "Referrals",
  operations: "Platform",
};

const EntryRow = ({
  entry,
  unseen,
  onOpen,
  onNavigate,
}: {
  entry: RailEntry;
  unseen: boolean;
  onOpen: () => void;
  onNavigate: () => void;
}) => {
  const hasSections = entry.sections.length > 0;
  const attention = railHasAttention(entry);
  const content = (
    <>
      <entry.icon
        weight={entry.isActive ? "duotone" : "regular"}
        className={cn({ [SIDEBAR_ICON_CURRENT]: entry.isActive })}
      />
      <span>{entry.label}</span>
      {unseen && (
        <Badge variant="outline" className="ml-auto text-ink/70">
          New
        </Badge>
      )}
      {attention && (
        <WarningIcon
          weight="fill"
          role="img"
          aria-label="Needs attention"
          className={cn("text-warning", { "ml-auto": !unseen })}
        />
      )}
      {hasSections && (
        <CaretRightIcon
          aria-hidden
          className={cn("text-subtle", { "ml-auto": !unseen && !attention })}
        />
      )}
    </>
  );
  return (
    <SidebarMenuItem>
      {hasSections ? (
        <SidebarMenuButton isActive={entry.isActive} onClick={onOpen}>
          {content}
        </SidebarMenuButton>
      ) : (
        <SidebarMenuButton
          isActive={entry.isActive}
          render={
            <Link
              to={entry.to}
              onClick={() => {
                entry.onNavigate?.();
                onNavigate();
              }}
              aria-current={entry.isActive ? "page" : undefined}
            />
          }
        >
          {content}
        </SidebarMenuButton>
      )}
    </SidebarMenuItem>
  );
};

const EntrySections = ({
  entry,
  onNavigate,
}: {
  entry: RailEntry;
  onNavigate: () => void;
}) => (
  <nav aria-label={entry.label} className="flex flex-1 flex-col">
    {railRowGroups(entry.sections).map((group, index) => (
      <SidebarGroup key={group.label ?? index} className="px-2 py-1">
        {group.label && (
          <SidebarGroupLabel className="h-7 px-2 text-ink/60">
            {group.label}
          </SidebarGroupLabel>
        )}
        <SidebarMenu>
          {group.rows.map((row) => (
            <SectionRow
              key={`${row.group ?? ""}:${row.to}`}
              row={row}
              onNavigate={() => {
                entry.onNavigate?.();
                onNavigate();
              }}
            />
          ))}
        </SidebarMenu>
      </SidebarGroup>
    ))}
  </nav>
);

// The phone drawer shows the same entries as the desktop rail. An entry with
// its own column opens it in place, with a back button to the full list, so
// the drawer never stacks the two tiers.
export const SidebarMobileNav = ({ account }: { account?: ReactNode }) => {
  const column = useNavColumn();
  const { openMobile, setOpenMobile } = useSidebar();
  const [openedId, setOpenedId] = useState(column.active?.id ?? null);
  const [wasOpen, setWasOpen] = useState(openMobile);
  if (wasOpen !== openMobile) {
    setWasOpen(openMobile);
    if (openMobile) setOpenedId(column.active?.id ?? null);
  }
  const entries = [
    ...column.productGroups.flatMap((group) => group.entries),
    ...column.utility,
  ];
  const opened =
    entries.find(
      (entry) => entry.id === openedId && entry.sections.length > 0,
    ) ?? null;
  const close = () => setOpenMobile(false);
  const row = (entry: RailEntry) => (
    <EntryRow
      key={entry.id}
      entry={entry}
      unseen={entry.sourceHrefs.some((href) => column.unseenHrefs.has(href))}
      onOpen={() => setOpenedId(entry.id)}
      onNavigate={close}
    />
  );

  return (
    <aside aria-label="Sidebar" className="flex min-h-0 flex-1 flex-col">
      <SidebarHeader className="relative flex-row items-center gap-1 overflow-hidden">
        {opened ? (
          <SidebarMenu className="min-w-0 flex-1">
            <SidebarMenuItem>
              <SidebarMenuButton
                className="pr-10"
                onClick={() => setOpenedId(null)}
              >
                <CaretLeftIcon />
                <span>Menu</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        ) : (
          <Link
            to="/"
            onClick={close}
            aria-label="Volca home"
            className="grid size-8 shrink-0 place-items-center rounded-sm outline-hidden focus-visible:ring-2 focus-visible:ring-focus max-md:size-9"
          >
            <LogoIcon width={22} height={18} />
          </Link>
        )}
        <span className="absolute top-2 right-2">
          <SidebarTrigger
            size="icon"
            className="pointer-coarse:size-11"
            aria-label="Close menu"
            aria-expanded={openMobile}
          />
        </span>
      </SidebarHeader>
      <CommandMenuTrigger />
      <SidebarContent className="overscroll-contain">
        {column.isLoading ? (
          <SidebarNavSkeleton />
        ) : opened ? (
          <>
            <SidebarGroup className="px-2 pt-1 pb-0">
              <SidebarGroupLabel className="h-7 px-2 text-sm font-medium text-ink">
                {opened.label}
              </SidebarGroupLabel>
            </SidebarGroup>
            <EntrySections entry={opened} onNavigate={close} />
          </>
        ) : (
          <nav aria-label="Main" className="flex flex-1 flex-col">
            {column.productGroups.map((group) => (
              <SidebarGroup key={group.id} className="px-2 py-1">
                <SidebarGroupLabel className="h-7 px-2 text-ink/60">
                  {GROUP_LABEL[group.id]}
                </SidebarGroupLabel>
                <SidebarMenu>{group.entries.map(row)}</SidebarMenu>
              </SidebarGroup>
            ))}
          </nav>
        )}
      </SidebarContent>
      {!opened && (
        <SidebarFooter className="px-2 pt-1 pb-2">
          {column.utility.length > 0 && (
            <nav aria-label="Workspace">
              <SidebarMenu>{column.utility.map(row)}</SidebarMenu>
            </nav>
          )}
          <SidebarMenu>
            <SidebarNavSupport />
            {account}
          </SidebarMenu>
        </SidebarFooter>
      )}
    </aside>
  );
};
